// store/marketSelectors.ts
import { createSelector } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';
import type { RootState } from '../../../store';
import { otcSlice } from '..';
import {
  MarketUI,
  Market,
  MarketRegistry,
  SymbolsIndex,
  SymbolMeta,
  HeaderChartData
} from './types';

// ---- Base selectors (slice roots) ----
const selectMarketState = (s: RootState) => s.otcMarket;

export const selectRegistry = (s: RootState): MarketRegistry =>
  selectMarketState(s).registry;
export const selectSymbols = (s: RootState): SymbolsIndex =>
  selectMarketState(s).symbols;
export const selectUI = (s: RootState): MarketUI => selectMarketState(s).ui;
export const selectInputs = (s: RootState) => selectMarketState(s).inputs;

// ---- Constants ----
const TMN = 'TMN';

// ---- Helper: Get universe of non-TMN online symbols ----
const universeNonTmnOnline = (reg: MarketRegistry): string[] => {
  const out = new Set<string>();
  for (const id of reg.ids) {
    const m = reg.byId[id];
    // eslint-disable-next-line no-continue
    if (!m || m.status !== 'online') continue;
    if (m.base !== TMN) out.add(m.base);
    if (m.quote !== TMN) out.add(m.quote);
  }
  return Array.from(out).sort();
};

// ---- Helper: Get partners for a symbol ----
function partnersForSymbol(
  reg: MarketRegistry,
  sym?: string,
  opts: { excludeTMN?: boolean; onlyOnline?: boolean } = {}
): Set<string> {
  const { excludeTMN = true, onlyOnline = true } = opts;
  if (!sym) return new Set<string>();

  const out = new Set<string>();
  const addNeighbor = (neighbor?: string) => {
    if (!neighbor || neighbor === sym) return;
    if (excludeTMN && neighbor === TMN) return;
    out.add(neighbor);
  };

  // sym as BASE -> add quotes
  for (const id of reg.idsByBase[sym] ?? []) {
    const m = reg.byId[id];
    // eslint-disable-next-line no-continue
    if (!m) continue;
    // eslint-disable-next-line no-continue
    if (onlyOnline && m.status !== 'online') continue;
    addNeighbor(m.quote);
  }

  // sym as QUOTE -> add bases
  for (const id of reg.idsByQuote[sym] ?? []) {
    const m = reg.byId[id];
    // eslint-disable-next-line no-continue
    if (!m) continue;
    // eslint-disable-next-line no-continue
    if (onlyOnline && m.status !== 'online') continue;
    addNeighbor(m.base);
  }

  return out;
}

// ---- Helper: Enrich symbol with metadata ----
const enrichSymbol = (symbol: string, symbolsIdx: SymbolsIndex): SymbolMeta => {
  const meta = symbolsIdx[symbol];
  return meta || { symbol };
};

// ---- Helper: Enrich symbol with metadata AND pair status ----
const enrichSymbolWithPairStatus = (
  symbol: string,
  symbolsIdx: SymbolsIndex,
  reg: MarketRegistry,
  paySymbol?: string
): SymbolMeta & { status: boolean } => {
  const meta = enrichSymbol(symbol, symbolsIdx);

  let status = false;
  if (paySymbol && symbol !== paySymbol) {
    const id1 = `${paySymbol}${symbol}`;
    const id2 = `${symbol}${paySymbol}`;
    const m = reg.byId[id1] || reg.byId[id2];
    status = !!(m && m.status === 'online');
  }

  return { ...meta, status };
};

// ---- Active Market Selectors ----
export const selectActiveMarketId = createSelector(
  [selectUI],
  (ui): string | undefined => ui.selectedMarketId
);

export const selectActiveMarket = createSelector(
  [selectRegistry, selectActiveMarketId],
  (reg, id): Market | undefined => (id ? reg.byId[id] : undefined),
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual // ← Is this present?
    }
  }
);

// ---- Raw Symbol Lists (no metadata) ----
const selectPaySymbolsRaw = createSelector(
  [selectRegistry, selectUI],
  (reg, ui): string[] => {
    if (ui.tab === 'buy') return [TMN];

    if (ui.tab === 'sell') {
      const ids = reg.idsByQuote.TMN ?? [];
      const bases = new Set<string>();
      for (const id of ids) {
        const m = reg.byId[id];
        // Same rule as the buy list: a base whose sell side is off is not offered.
        if (m && m.sell?.enabled) bases.add(m.base);
      }
      return Array.from(bases).sort();
    }

    // EXCHANGE: full universe (non-TMN), online only
    return universeNonTmnOnline(reg);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual // ← Is this present?
    }
  }
);

const selectReceiveSymbolsRaw = createSelector(
  [selectRegistry, selectUI],
  (reg, ui): string[] => {
    if (ui.tab === 'buy') {
      // receive = BASE symbols of TMN markets that are tradable for buy
      const ids = reg.idsByQuote[TMN] ?? [];
      const bases = new Set<string>();
      for (const id of ids) {
        const m = reg.byId[id];
        if (m && m.buy?.enabled) bases.add(m.base);
      }
      return Array.from(bases);
    }

    if (ui.tab === 'sell') {
      return [TMN];
    }

    // EXCHANGE: If pay chosen -> receive = partners(pay)
    if (ui.paySymbol) {
      const partners = partnersForSymbol(reg, ui.paySymbol, {
        excludeTMN: true,
        onlyOnline: false
      });
      partners.delete(ui.paySymbol);
      return Array.from(partners);
    }

    // Otherwise -> show universe
    return universeNonTmnOnline(reg);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual // ← Is this present?
    }
  }
);

// ---- ENRICHED Pay/Receive Lists (with metadata) ----

/**
 * Pay options with full symbol metadata
 * For BUY: [TMN with metadata]
 * For SELL: [all tradable bases with metadata]
 * For EXCHANGE: [all non-TMN online symbols with metadata]
 */
export const selectPayOptions = createSelector(
  [selectPaySymbolsRaw, selectSymbols],
  (symbols, symbolsIdx): SymbolMeta[] => {
    const syms = symbols
      .map((symbol) => enrichSymbol(symbol, symbolsIdx))
      .sort((a, b) => (a?.order || 0) - (b?.order || 0));
    return syms;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual // ← ADD THIS
    }
  }
);

/**
 * Receive options with full symbol metadata
 * For BUY: [all buy-enabled bases with metadata]
 * For SELL: [TMN with metadata]
 * For EXCHANGE: [partner symbols with metadata and pair status]
 */
export const selectReceiveOptions = createSelector(
  [selectReceiveSymbolsRaw, selectSymbols, selectRegistry, selectUI],
  (symbols, symbolsIdx, reg, ui): (SymbolMeta & { status?: boolean })[] => {
    // For exchange with pay selected, add status
    if (ui.tab === 'exchange' && ui.paySymbol) {
      return symbols
        .map((symbol) =>
          enrichSymbolWithPairStatus(symbol, symbolsIdx, reg, ui.paySymbol)
        )
        .sort((a, b) => (a?.order || 0) - (b?.order || 0));
    }

    // Otherwise just enrich with metadata
    return symbols
      .map((symbol) => enrichSymbol(symbol, symbolsIdx))
      .sort((a, b) => (a?.order || 0) - (b?.order || 0));
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual // ← ADD THIS
    }
  }
);

// ---- Resolved symbols & direction ----
export const selectResolvedSymbols = createSelector(
  [selectUI, selectActiveMarket],
  (ui, m) => {
    if (!m) {
      return {
        pay: undefined as string | undefined,
        receive: undefined as string | undefined,
        payIsQuote: ui.tab === 'buy'
      };
    }

    if (ui.tab === 'buy') {
      return { pay: m.quote, receive: m.base, payIsQuote: true };
    }

    if (ui.tab === 'sell') {
      return { pay: m.base, receive: m.quote, payIsQuote: false };
    }

    // EXCHANGE: preserve user choice
    let pay = ui.paySymbol;
    let receive = ui.receiveSymbol;

    const inMarket = (s?: string) => !!s && (s === m.base || s === m.quote);

    if (!pay && inMarket(receive)) {
      pay = receive === m.base ? m.quote : m.base;
    }
    if (!receive && inMarket(pay)) {
      receive = pay === m.quote ? m.base : m.quote;
    }

    const payIsQuote = !!pay && pay === m.quote;

    return { pay, receive, payIsQuote };
  }
);

// ---- Default Values ----
export const selectDefaultValues = createSelector(
  [selectActiveMarket, selectUI, selectResolvedSymbols],
  (m, ui, resolved) => {
    if (!m) return { pay: '', receive: '' };

    if (ui.tab === 'buy') {
      const receive = m.buy?.defaultBaseValue || '1';
      return { pay: '', receive };
    }

    if (ui.tab === 'sell') {
      const pay = m.sell?.defaultBaseValue || '1';
      return { pay, receive: '' };
    }

    // EXCHANGE: default depends on direction
    const pay = resolved.payIsQuote
      ? m.buy?.defaultQuoteValue || ''
      : m.sell?.defaultBaseValue || '1';

    return { pay, receive: '' };
  }
);

// Add these to your selectors.ts file

/**
 * Select the currently active pay symbol with full metadata
 */
export const selectCurrentPaySymbol = createSelector(
  [selectResolvedSymbols, selectSymbols],
  (resolved, symbolsIdx): SymbolMeta | undefined => {
    if (!resolved.pay) return undefined;
    return enrichSymbol(resolved.pay, symbolsIdx);
  }
);

/**
 * Select the currently active receive symbol with full metadata
 */
export const selectCurrentReceiveSymbol = createSelector(
  [selectResolvedSymbols, selectSymbols],
  (resolved, symbolsIdx): SymbolMeta | undefined => {
    if (!resolved.receive) return undefined;
    return enrichSymbol(resolved.receive, symbolsIdx);
  }
);

/**
 * Complete pay asset data for the calculator UI
 * Returns everything the UI needs to render pay input
 */
export const selectPayAssetData = createSelector(
  [
    selectCurrentPaySymbol,
    selectActiveMarket,
    selectActiveMarketId,
    selectResolvedSymbols
  ],
  (paySymbol, market, marketId, resolved) => {
    if (!paySymbol || !market || !marketId) return null;

    const isBuy = resolved.payIsQuote;

    return {
      id: marketId,
      symbol: paySymbol.symbol,
      nameFa: paySymbol.nameFa,
      nameEn: paySymbol.nameEn,
      logo: paySymbol.logo,
      basePrecision: market.precisions.base,
      quotePrecision: market.precisions.quote,
      tick: market.precisions.priceTick,
      isBuy,
      mode: isBuy ? ('buy' as const) : ('sell' as const),
      market: marketId,
      status: isBuy ? market.buy.enabled : market.sell.enabled,
      commissionPercentage: market.fees.commissionPct,
      minBuy: market.buy.min,
      maxBuy: market.buy.max,
      minSell: market.sell.min,
      maxSell: market.sell.max
    };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual // ← ADD THIS
    }
  }
);

/**
 * Complete receive asset data for the calculator UI
 * Returns everything the UI needs to render receive input
 */
export const selectReceiveAssetData = createSelector(
  [
    selectCurrentReceiveSymbol,
    selectActiveMarket,
    selectActiveMarketId,
    selectResolvedSymbols
  ],
  (receiveSymbol, market, marketId, resolved) => {
    if (!receiveSymbol || !market || !marketId) return null;

    const isBuy = resolved.payIsQuote;

    return {
      id: marketId,
      symbol: receiveSymbol.symbol,
      nameFa: receiveSymbol.nameFa,
      nameEn: receiveSymbol.nameEn,
      logo: receiveSymbol.logo,
      basePrecision: market.precisions.base,
      quotePrecision: market.precisions.quote,
      tick: market.precisions.priceTick,
      isBuy,
      mode: isBuy ? ('buy' as const) : ('sell' as const),
      market: marketId,
      status: isBuy ? market.buy.enabled : market.sell.enabled,
      commissionPercentage: market.fees.commissionPct,
      minBuy: market.buy.min,
      maxBuy: market.buy.max,
      minSell: market.sell.min,
      maxSell: market.sell.max,
      min: isBuy ? market.buy.min : market.sell.min,
      max: isBuy ? market.buy.max : market.sell.max
    };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual // ← ADD THIS
    }
  }
);

export const selectChartData = createSelector(
  [
    selectCurrentReceiveSymbol,
    selectCurrentPaySymbol,
    selectResolvedSymbols,
    selectActiveMarket
  ],
  (receiveSymbol, paySymbol, resolved, market): HeaderChartData | null => {
    if (!receiveSymbol || !market || !paySymbol) return null;

    const isBuy = resolved.payIsQuote;

    // isBuy:  pay=QUOTE(TMN), receive=BASE(BTC) -> base=BTC, quote=TMN
    // !isBuy: pay=BASE(BTC), receive=QUOTE(TMN) -> base=BTC, quote=TMN
    const baseSymbol = isBuy ? receiveSymbol : paySymbol;
    const quoteSymbol = isBuy ? paySymbol : receiveSymbol;

    return {
      id: market.id,
      marketName: market.id,
      base: {
        symbol: baseSymbol.symbol,
        nameFa: baseSymbol.nameFa, // ✅ FIXED
        nameEn: baseSymbol.nameEn, // ✅ FIXED
        logo: baseSymbol.logo,
        precision: market.precisions.base.toString() // ✅ Convert to string if needed
      },
      quote: {
        symbol: quoteSymbol.symbol,
        nameFa: quoteSymbol.nameFa, // ✅ FIXED
        nameEn: quoteSymbol.nameEn, // ✅ FIXED
        logo: quoteSymbol.logo,
        precision: market.precisions.quote.toString() // ✅ Convert to string if needed
      }
    };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual // ← ADD THIS
    }
  }
);

// services/otc/market/selectors.ts

// ✅ NEW: Select only the prices we care about
export const selectRelevantPrices = createSelector(
  [
    selectActiveMarketId,
    selectResolvedSymbols,
    (state: RootState) =>
      otcSlice.endpoints.getOtcMarketTicker.select(null)(state)?.data?.data
  ],
  (marketId, resolved, tickerData) => {
    if (!marketId || !tickerData) {
      return {
        currentMarketPrice: null,
        usdtPrice: null,
        marketSymbol: null,
        usdtSymbol: null
      };
    }

    // Build map once (we still need this, but result is memoized)
    const symbolMap = new Map<string, any>();
    for (const row of tickerData) {
      const key = (row?.s || '').replace(/-/g, '').toUpperCase();
      if (key) symbolMap.set(key, row);
    }

    // Get our market
    const normalizedMarketId = marketId.replace(/-/g, '').toUpperCase();
    const marketData = symbolMap.get(normalizedMarketId);

    // Get USDT price for base asset
    const baseSymbol = resolved.payIsQuote ? resolved.receive : resolved.pay;
    const usdtSymbol =
      baseSymbol && baseSymbol !== 'TMN' ? `${baseSymbol}USDT` : null;
    const usdtData = usdtSymbol ? symbolMap.get(usdtSymbol) : null;

    return {
      // Extract just the numbers we need
      currentMarketPrice: marketData
        ? {
            // Same names as the Android app (priceBuy / priceSell): the price
            // the user pays to buy / receives when selling.
            buyPrice: Number(marketData.B),
            sellPrice: Number(marketData.S)
          }
        : null,
      usdtPrice: usdtData ? Number(usdtData.R) : null,
      marketSymbol: normalizedMarketId,
      usdtSymbol
    };
  },
  {
    memoizeOptions: {
      // ✅ CRITICAL: Only return new object if prices actually changed!
      resultEqualityCheck: (prev, next) => {
        return (
          prev.marketSymbol === next.marketSymbol &&
          prev.currentMarketPrice?.buyPrice ===
            next.currentMarketPrice?.buyPrice &&
          prev.currentMarketPrice?.sellPrice ===
            next.currentMarketPrice?.sellPrice &&
          prev.usdtPrice === next.usdtPrice
        );
      }
      // More advance Equality check
      /* resultEqualityCheck: (prev, next) => {
         // Only count as "changed" if price moved by more than 0.01%
         const bidChanged = Math.abs(
           (next.currentMarketPrice?.bid || 0) - (prev.currentMarketPrice?.bid || 0)
         ) / (prev.currentMarketPrice?.bid || 1) > 0.0001;
 
         const askChanged = Math.abs(
           (next.currentMarketPrice?.ask || 0) - (prev.currentMarketPrice?.ask || 0)
         ) / (prev.currentMarketPrice?.ask || 1) > 0.0001;
 
         return !bidChanged && !askChanged;
       } */
    }
  }
);
